import React from 'react'
import { subscribe } from 'react-contextual'
import { InvoicesProvider } from '../../../../../providers'
import { depositInvoiceTypes } from 'modules/api/invoice/constants'
import { CurrencyField, FundDistribution, Segment, TextField, TextInput } from '../../../../../components'
import { TextFieldTypes } from 'views/components/atoms/TextField/text-field.types'
import styles from './PaymentAllocation.module.scss'

interface InvoiceType {
  name: string | null
  value: string | null
}

interface BeneficiaryValue {
  name?: string
  partyId?: string
  partyTag?: string
  reference?: string
  amount?: number | string
  vat?: boolean
}

interface Beneficiary {
  beneficiary?: {
    type?: string
    value?: BeneficiaryValue
  }
}

interface PartyTag {
  value: string
  label: string
}

interface PaymentAllocationProps {
  invoiceId?: string
  invoiceType?: InvoiceType
  amount: string
  onAmountChange?: (e: any) => any
  onVatChange?: (...args: any[]) => any
  amountVatApplied?: boolean
  onNewBeneficiaryAdded?: (beneficiary: any) => any
  onBeneficiaryRemoved?: (...args: any[]) => any
  onNewBeneficiaryAmountChange?: (...args: any[]) => any
  onDepositBeneficiaryTransferChange: (...args: any[]) => any
  portfolioId?: string
  invoiceStatus?: string
  beneficiaries?: Beneficiary[]
  partyTags?: PartyTag[]
  propertyOwnerId: string
  propertyOwnerName: string
  propertyOwnerBankDetails?: object
  getOwnerAccountFromDraftInvoiceId?: (...args: any[]) => any
  description?: string
  onDescriptionChange?: (...args: any[]) => any
  unallocatedAmount: number
  /** Injected by the InvoicesProvider subscription */
  easyPayData?: any
  fetchEasyPayData?: (...args: any[]) => any
  [key: string]: any
}

interface AmountProps {
  amount: string
  invoiceType?: string | null
  invoiceTypeLabel?: string | null
  onAmountChange?: (e: any) => any
  onVatChange?: (...args: any[]) => any
  amountVatApplied?: boolean
  invoiceStatus?: string
}

const Amount = ({
  amount,
  invoiceType,
  invoiceTypeLabel,
  onAmountChange,
  onVatChange,
  amountVatApplied,
}: AmountProps): React.ReactElement => {
  return (
    <Segment horizontal state={parseFloat(amount) > 0 ? 'default' : 'warning'}>
      <Segment.Label text={invoiceTypeLabel || ''} />
      <div className={styles.amount}>
        <CurrencyField
          includeVatControl
          name="amount"
          placeholder="Enter amount"
          onChange={onAmountChange}
          value={amount}
          onToggleChange={onVatChange}
          vatApplied={depositInvoiceTypes.includes(invoiceType ?? '') ? false : amountVatApplied}
          isEditVatDisabled={depositInvoiceTypes.includes(invoiceType ?? '')}
        />
      </div>
    </Segment>
  )
}

const PaymentAllocation = (props: PaymentAllocationProps): React.ReactElement => {
  const {
    invoiceId,
    invoiceType = { name: null, value: null },
    amount,
    onAmountChange = (e: any) => e,
    beneficiaries = [],
    invoiceStatus,
    onNewBeneficiaryAdded = (beneficiary: any) => beneficiary,
    onBeneficiaryRemoved,
    onNewBeneficiaryAmountChange,
    onDepositBeneficiaryTransferChange,
    onBeneficiariesOrderChange,
    errors,
    onVatChange,
    amountVatApplied,
    onBeneficiaryVatChange,
    partyTags,
    easyPayData,
    fetchEasyPayData,
    propertyOwnerId,
    propertyOwnerName,
    propertyOwnerBankDetails,
    description,
    onDescriptionChange,
    unallocatedAmount,
    portfolioId,
    ...rest
  } = props

  return (
    <div className={styles.root}>
      <FundDistribution
        invoiceType={invoiceType}
        headerText="Payment Allocation"
        beforeContent={
          <Segment className={styles['invoice-segment']}>
            <Amount
              amount={amount}
              invoiceType={invoiceType.value}
              invoiceTypeLabel={invoiceType.name}
              onVatChange={onVatChange}
              invoiceStatus={invoiceStatus}
              onAmountChange={onAmountChange}
              amountVatApplied={amountVatApplied}
            />
            <Segment horizontal className={styles.description}>
              <TextField
                inputComponent={
                  <TextInput type={TextFieldTypes.text} value={description} onChange={onDescriptionChange} />
                }
                label="Description"
              />
            </Segment>
          </Segment>
        }
        balance={parseFloat(amount)}
        onNewBeneficiaryAdded={onNewBeneficiaryAdded}
        onBeneficiaryRemoved={onBeneficiaryRemoved}
        onNewBeneficiaryAmountChange={onNewBeneficiaryAmountChange}
        onDepositBeneficiaryTransferChange={onDepositBeneficiaryTransferChange}
        onBeneficiariesOrderChange={onBeneficiariesOrderChange}
        invoiceStatus={invoiceStatus}
        beneficiaries={beneficiaries}
        partySearchTags=""
        partyTags={partyTags}
        errors={errors}
        onVatChange={onBeneficiaryVatChange}
        easyPayData={easyPayData}
        fetchEasyPayData={fetchEasyPayData}
        propertyOwnerId={propertyOwnerId}
        propertyOwnerName={propertyOwnerName}
        propertyOwnerBankDetails={propertyOwnerBankDetails}
        disableDepositEdit={false}
        isDepositAmountDerivedFromInvoice={depositInvoiceTypes.includes(invoiceType.value ?? '')}
        unallocatedAmount={unallocatedAmount}
        portfolioId={portfolioId}
        {...rest}
      />
    </div>
  )
}

export default subscribe(
  [InvoicesProvider],
  ({ fetchEasyPayData, easyPayData, propertyOwnerId, propertyOwnerName, propertyOwnerBankDetails }: any) => ({
    fetchEasyPayData,
    easyPayData,
    propertyOwnerId,
    propertyOwnerName,
    propertyOwnerBankDetails,
  }),
)(PaymentAllocation)
